import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Package, PackageX, Save, X, Trash2, Loader2 } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const ToggleRow = ({ label, value, onChange, onIcon, offIcon, testId }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-center gap-2 text-foreground">
      {value ? onIcon : offIcon}
      <span className="text-sm font-medium">{label}</span>
    </div>
    <Switch checked={value} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

export const ProductRow = ({ product, onUpdate, onDelete, saving, deleting }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: product.name,
    price: product.price,
    category: product.category || '',
    image_url: product.image_url || '',
  });

  // Toggles update immediately (no edit mode)
  const handleToggle = (field) => (value) => onUpdate(product.id, { [field]: value });

  const startEdit = () => {
    setDraft({
      name: product.name,
      price: product.price,
      category: product.category || '',
      image_url: product.image_url || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    await onUpdate(product.id, {
      name: draft.name,
      price: parseFloat(draft.price),
      category: draft.category,
      image_url: draft.image_url || null,
    });
    setEditing(false);
  };

  const isVisible = product.is_visible !== false;
  const inStock = product.in_stock !== false;

  return (
    <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Card
        data-testid={`admin-product-row-${product.id}`}
        className={`p-5 bg-card border ${isVisible ? 'border-border' : 'border-border/40 opacity-70'}`}
      >
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start">
          <div className="space-y-4 min-w-0">
            {editing ? (
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Naam">
                  <Input
                    value={draft.name}
                    onChange={e => setDraft({ ...draft, name: e.target.value })}
                    data-testid={`admin-product-name-${product.id}`}
                  />
                </Field>
                <Field label="Prijs (€)">
                  <Input
                    type="number" step="0.01" min="0"
                    value={draft.price}
                    onChange={e => setDraft({ ...draft, price: e.target.value })}
                    data-testid={`admin-product-price-${product.id}`}
                  />
                </Field>
                <Field label="Categorie">
                  <Input
                    value={draft.category}
                    onChange={e => setDraft({ ...draft, category: e.target.value })}
                    placeholder="bv. burger / wings"
                    data-testid={`admin-product-category-${product.id}`}
                  />
                </Field>
                <Field label="Afbeelding URL">
                  <Input
                    value={draft.image_url}
                    onChange={e => setDraft({ ...draft, image_url: e.target.value })}
                    placeholder="https://..."
                    data-testid={`admin-product-image-${product.id}`}
                  />
                </Field>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                {product.image_url && (
                  <img src={product.image_url} alt={product.name} className="w-14 h-14 rounded-lg object-cover bg-muted" />
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="text-xl font-bold text-foreground truncate">{product.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {product.category || '\u2014'} · €{Number(product.price).toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {editing ? (
                <>
                  <Button
                    size="sm"
                    onClick={saveEdit}
                    disabled={saving}
                    data-testid={`admin-product-save-${product.id}`}
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Opslaan
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                    <X className="w-4 h-4 mr-1" /> Annuleer
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm" variant="outline" onClick={startEdit}
                    data-testid={`admin-product-edit-${product.id}`}
                  >
                    Bewerken
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDelete(product.id, product.name)}
                    disabled={deleting}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/30"
                    data-testid={`admin-product-delete-${product.id}`}
                  >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="md:w-56 md:border-l md:pl-6 md:border-border/60 space-y-1">
            <ToggleRow
              label={isVisible ? 'Zichtbaar in kiosk' : 'Verborgen'}
              value={isVisible}
              onChange={handleToggle('is_visible')}
              onIcon={<Eye className="w-4 h-4 text-primary" />}
              offIcon={<EyeOff className="w-4 h-4 text-muted-foreground" />}
              testId={`admin-toggle-visible-${product.id}`}
            />
            <ToggleRow
              label={inStock ? 'Op voorraad' : 'Uitverkocht'}
              value={inStock}
              onChange={handleToggle('in_stock')}
              onIcon={<Package className="w-4 h-4 text-primary" />}
              offIcon={<PackageX className="w-4 h-4 text-red-400" />}
              testId={`admin-toggle-stock-${product.id}`}
            />
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default ProductRow;
